import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import EmployeesPage from "./pages/EmployeesPage";
import VerticalsPage from "./pages/VerticalsPage";
import SalaryReportsPage from "./pages/SalaryReportsPage";
import RetentionBonusPage from "./pages/RetentionBonusPage";
import AnnualBonusPage from "./pages/AnnualBonusPage";
import ReportsPage from "./pages/ReportsPage";
import IncrementsPage from "./pages/IncrementsPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import EmployeeSalaryReportPage from "./pages/EmployeeSalaryReportPage";
import AdvancesPage from "./pages/AdvancesPage";
import ZohoIntegrationPage from "./pages/ZohoIntegrationPage";
import ZohoDashboardPage from "./pages/ZohoDashboardPage";
import ProtoSubmissionsPage from "./components/zoho/proto/ProtoSubmissionsPage";
import ProtoIncomeTaxUI from "./components/zoho/proto/ProtoIncomeTaxUI";
import ZohoSubmissionsPage from "./pages/ZohoSubmissionsPage";
import ResignedSalaryPage from "./pages/ResignedSalaryPage";
import ResignedEmployeeDocsPage from "./pages/ResignedEmployeeDocsPage";
import DocsHubPage from "./pages/DocsHubPage";
import RetentionBonusDocsPage from "./pages/RetentionBonusDocsPage";
import PenaltyDocsPage from "./pages/PenaltyDocsPage";
import KekaUploadPage from "./pages/KekaUploadPage";
import ChangelogPage from "./pages/ChangelogPage";
import LegalSalarySlipPage from "./pages/LegalSalarySlipPage";
import BlogicSalaryEntryPage from "./pages/BlogicSalaryEntryPage";
import NonBlogicSalaryEntryPage from "./pages/NonBlogicSalaryEntryPage";
import EPFReportPage from "./pages/EPFReportPage";
import EmployeeDocsPage from "./pages/EmployeeDocsPage";
import PenaltiesPage from "./pages/PenaltiesPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/organizations"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager"]}>
            <OrganizationsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/employees"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager"]}>
            <EmployeesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/verticals"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager"]}>
            <VerticalsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/salary-reports"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager", "payroll_auditor", "account_executive"]}>
            <NonBlogicSalaryEntryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/blogic-salary-entry"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager", "payroll_auditor", "account_executive"]}>
            <BlogicSalaryEntryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "payroll_auditor", "account_executive"]}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/resigned-employees"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager", "payroll_auditor", "account_executive"]}>
            <ResignedSalaryPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/retention-bonus"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "payroll_auditor", "account_executive"]}>
            <RetentionBonusPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/annual-bonus"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "payroll_auditor", "account_executive"]}>
            <AnnualBonusPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/docs"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager", "payroll_auditor", "account_executive"]}>
            <DocsHubPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/employee-docs"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager", "payroll_auditor", "account_executive"]}>
            <EmployeeDocsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/docs/exit-settlement"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager", "payroll_auditor", "account_executive"]}>
            <ResignedEmployeeDocsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/docs/retention-bonus"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager", "payroll_auditor", "account_executive"]}>
            <RetentionBonusDocsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/docs/penalties"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager", "payroll_auditor", "account_executive"]}>
            <PenaltyDocsPage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/penalties"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "lop_manager"]}>
            <PenaltiesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/increments"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "payroll_auditor"]}>
            <IncrementsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "payroll_auditor"]}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <UsersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "payroll_auditor"]}>
            <AuditLogsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/employee-salary-report"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "payroll_auditor", "account_executive"]}>
            <EmployeeSalaryReportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/advances"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "payroll_auditor"]}>
            <AdvancesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/zoho-integration"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <ZohoIntegrationPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/zoho-dashboard"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <ZohoDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/zoho-submissions/:formId"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <ZohoSubmissionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/proto-submissions"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <ProtoSubmissionsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/proto-income-tax"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <ProtoIncomeTaxUI />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/keka-upload" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin', 'lop_manager']}>
          <KekaUploadPage />
        </ProtectedRoute>
      } />
      <Route path="/changelog" element={
        <ProtectedRoute>
          <ChangelogPage />
        </ProtectedRoute>
      } />
      <Route path="/legal/salary-slip" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin', 'lop_manager']}>
          <LegalSalarySlipPage />
        </ProtectedRoute>
      } />
      <Route path="/epf-report" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
          <EPFReportPage />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
